import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { config } from '../../config';
import { Schedule } from '../../types/schedule';
import { format } from 'date-fns';

interface ShiftRecord {
  _id: string;
  locationId: {
    _id: string;
    name: string;
  };
  startTime: string;
  endTime?: string;
  status: 'active' | 'completed';
}

const EmployeeSchedulePage: React.FC = () => {
  const { user } = useAuth();
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [activeShift, setActiveShift] = useState<ShiftRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSchedules();
    checkShiftStatus();
  }, []);

  const fetchSchedules = async () => {
    try {
      const response = await axios.get(`${config.apiUrl}/api/schedules/employee`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSchedules(response.data);
    } catch (err) {
      setError('Failed to load schedules');
    } finally {
      setIsLoading(false);
    }
  };

  const checkShiftStatus = async () => {
    try {
      const response = await axios.get(`${config.apiUrl}/api/shifts/status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setActiveShift(response.data.activeShift);
    } catch (err) {
      console.error('Failed to check shift status:', err);
    }
  };

  const startShift = async (locationId: string) => {
    try {
      const response = await axios.post(
        `${config.apiUrl}/api/shifts/start`,
        { locationId },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setActiveShift(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start shift');
    }
  };

  const endShift = async () => {
    try {
      await axios.post(
        `${config.apiUrl}/api/shifts/end`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setActiveShift(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to end shift');
    }
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">My Schedule</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {activeShift && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          <p>Current shift started at: {format(new Date(activeShift.startTime), 'PPpp')}</p>
          <p>Location: {activeShift.locationId.name}</p>
          <button
            onClick={endShift}
            className="mt-2 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
          >
            End Shift
          </button>
        </div>
      )}

      <div className="grid gap-4">
        {schedules.map((schedule) => (
          <div
            key={schedule._id}
            className="border rounded-lg p-4 bg-white shadow"
          >
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-semibold">{schedule.location}</h3>
                <p className="text-gray-600">
                  {schedule.day} • {schedule.startTime} - {schedule.endTime}
                </p>
              </div>
              {!activeShift && (
                <button
                  onClick={() => startShift(schedule.locationId)}
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  Start Shift
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EmployeeSchedulePage;
